// src/components/iamConsole/slr/operations/PrismaModal.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from "react-dom";
import axios from 'axios';
import ReactFlow, {
  Controls,
  MarkerType,
  Position,
  Handle,
  ReactFlowProvider,
} from 'reactflow';
import domtoimage from 'dom-to-image';
import 'reactflow/dist/style.css';
import GetCookie from '../../../../utils/GetCookie';
import ProcessingPopup from '../../../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../../../utils/popup/SuccessPopup';

// --- Custom Exclusion Node (scrollable reasons, wider) ---
const EXCL_BOX_STYLE = {
  background: 'var(--color10)',
  border: '2px solid var(--color2)',
  borderRadius: 15,
  width: 570,
  minHeight: 60,
  padding: 15,
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
};

const ExclusionNode = ({ data }) => {
  const localText = useRef('Records excluded');
  const localCount = useRef(data.totalExcluded ?? 0);
  const localReasons = useRef((data.reasons || []).map(r => ({ ...r })));
  const reasons = data.reasons || [];

  if (!data.isEditMode) {
    localCount.current = data.totalExcluded ?? 0;
    localReasons.current = (data.reasons || []).map(r => ({ ...r }));
  }

  return (
    <div style={{ position: 'relative' }}>
      <Handle type="target" position={Position.Left} id="left" style={{ left: 0, top: '50%', opacity: 0 }} />
      <div className="nodrag nopan" style={{ ...EXCL_BOX_STYLE, cursor: 'default', overflow: 'hidden' }}>
        {!data.isEditMode ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color5)', lineHeight: 1.4, textAlign: 'center' }}>
              {localText.current} (n = {data.totalExcluded ?? 0})
            </div>
            {reasons.length > 0 && (
              <div style={{ textAlign: 'left', marginTop: 6, fontSize: 12, lineHeight: 1.35, color: 'var(--color5)' }}>
                {reasons.filter(r => r.reason && r.reason.trim()).map((r, i) => (
                  <div key={i} style={{ marginBottom: 1 }}>• {r.reason} (n = {r.count})</div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <input data-field="label" type="text" defaultValue={localText.current} onChange={e => { localText.current = e.target.value; }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} className="nodrag nopan" style={{ width: '95%', fontSize: 11, borderRadius: 4, border: '1px solid var(--color2)', padding: '1px 4px', background: 'var(--background1)', color: 'var(--color5)', textAlign: 'center' }} />
            <input data-field="count" type="number" defaultValue={localCount.current} onChange={e => { localCount.current = Number(e.target.value); }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} className="nodrag nopan" style={{ width: 70, fontSize: 11, borderRadius: 4, border: '1px solid var(--color2)', padding: '1px 4px', background: 'var(--background1)', color: 'var(--color5)', textAlign: 'center' }} />
            {localReasons.current.filter(r => r.reason && r.reason.trim()).map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 4, width: '95%', alignItems: 'center' }}>
                <input data-field={`reason_text_${i}`} type="text" defaultValue={r.reason} onChange={e => { localReasons.current[i].reason = e.target.value; }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} className="nodrag nopan" style={{ flex: 1, fontSize: 10, borderRadius: 4, border: '1px solid var(--color2)', padding: '1px 4px', background: 'var(--background1)', color: 'var(--color5)' }} />
                <input data-field={`reason_count_${i}`} type="number" defaultValue={r.count} onChange={e => { localReasons.current[i].count = Number(e.target.value); }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} className="nodrag nopan" style={{ width: 45, fontSize: 10, borderRadius: 4, border: '1px solid var(--color2)', padding: '1px 4px', background: 'var(--background1)', color: 'var(--color5)', textAlign: 'center' }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
// --- Custom Biblio Node (all state controlled from parent) ---
const BiblioNode = ({ data }) => {
  const localCount = useRef(data.count ?? 0);
  const localText = useRef('Bibliographic and conference searching');

  if (!data.isEditing && !data.isEditMode) {
    localCount.current = data.count ?? 0;
  }

  return (
    <div className="nodrag nopan" style={{ background: 'var(--color10)', border: '2px solid var(--color2)', borderRadius: 15, minWidth: 180, minHeight: 60, padding: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', position: 'relative', textAlign: 'center', cursor: (data.isEditing || data.isEditMode) ? 'text' : 'pointer' }}>'
      <Handle type="source" position={Position.Right} id="right" style={{ right: 0, top: '50%', transform: 'translateY(-50%)', opacity: 0 }} />

      {(!data.isEditing && !data.isEditMode) ? (
        <>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color5)', lineHeight: 1.5 }}>{localText.current}</div>
          <div style={{ fontSize: 13, color: 'var(--color5)', marginTop: 4 }}>
            (n = {data.count ?? 0})
            {data.saved && <span style={{ fontSize: 10, color: 'green', marginLeft: 4 }}>Saved</span>}
          </div>
        </>
      ) : (
        <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
          <input
            data-field="label"
            type="text"
            defaultValue={localText.current}
            onChange={e => { localText.current = e.target.value; }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            className="nodrag nopan"
            style={{ width: '100%', fontSize: 12, borderRadius: 6, border: '1px solid var(--color2)', padding: '2px 6px', background: 'var(--background1)', color: 'var(--color5)', textAlign: 'center', marginBottom: 6 }}
          />
          <input
            data-field="count"
            type="number"
            defaultValue={localCount.current}
            onChange={e => { localCount.current = Number(e.target.value); if (data.isEditing) data.onChange && data.onChange(localCount.current); }}
            onBlur={() => { if (data.isEditing) data.onBlur && data.onBlur(); }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') { if (data.isEditing) data.onBlur && data.onBlur(); } }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            autoFocus={data.isEditing}
            className="nodrag nopan"
            style={{ width: 80, fontSize: 13, borderRadius: 6, border: '1px solid var(--color2)', padding: '2px 6px', background: 'var(--background1)', color: 'var(--color5)', textAlign: 'center' }}
          />
          {data.saved && <div style={{ fontSize: 10, color: 'green', marginTop: 2 }}>Saved ✓</div>}
        </div>
      )}
    </div>
  );
};

const EditableNode = ({ data }) => {
  const localText = useRef(data.label ?? '');
  const localCount = useRef(data.count ?? 0);

  if (!data.isEditMode) {
    localText.current = data.label ?? '';
    localCount.current = data.count ?? 0;
  }

  return (
    <div style={{ position: 'relative' }}>
      {data.handles}
      <div
        className="nodrag nopan"
        style={{ background: 'var(--color10)', border: '2px solid var(--color2)', borderRadius: 15, width: 310, minHeight: 60, padding: 12, textAlign: 'center', cursor: 'default' }}
      >
        {!data.isEditMode ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color5)', lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: data.label }} />
            <div style={{ fontSize: 13, color: 'var(--color5)', marginTop: 4 }}>(n = {data.count ?? 0})</div>
          </>
        ) : (
          <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <input data-field="label" type="text" defaultValue={localText.current} onChange={e => { localText.current = e.target.value; }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} className="nodrag nopan" style={{ width: '100%', fontSize: 12, borderRadius: 6, border: '1px solid var(--color2)', padding: '2px 6px', background: 'var(--background1)', color: 'var(--color5)', textAlign: 'center', marginBottom: 6 }} />
            <input data-field="count" type="number" defaultValue={localCount.current} onChange={e => { localCount.current = Number(e.target.value); }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} className="nodrag nopan" style={{ width: 80, fontSize: 12, borderRadius: 6, border: '1px solid var(--color2)', padding: '2px 6px', background: 'var(--background1)', color: 'var(--color5)', textAlign: 'center' }} />
          </div>
        )}
      </div>
    </div>
  );
};
// --- React Flow Config ---
const nodeDefaults = {
  draggable: false,
  connectable: false,
  selectable: false,
};

const editableNodeDefaults = {
  draggable: false,
  connectable: false,
  selectable: true,
};

// --- Edge style constant ---
const edgeStyle = { strokeWidth: 2, stroke: 'var(--color2)' };
const edgeMarker = { type: MarkerType.ArrowClosed, color: 'var(--color2)' };

// --- Stage label style (vertical text on left side) ---
const stageLabelStyle = {
  background: 'var(--color10)',
  border: '2px solid var(--color2)',
  borderRadius: 15,
  width: 45,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  padding: 0,
};

const stageLabelTextStyle = {
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--color2)',
  textAlign: 'center',
  padding: '10px 4px',
  whiteSpace: 'nowrap',
};

// --- Dynamic Node Position Calculator ---
const getNodePositions = () => {
  return {
    // Stage labels (left side, vertical)
    label1: { x: -300, y: 10 },
    label2: { x: -300, y: 170 },
    label3: { x: -300, y: 470 },
    label4: { x: -300, y: 640 },

    // Main flow boxes (center column, vertical)
    box1: { x: 180, y: 10 },
    box2: { x: 180, y: 200 },
    box4: { x: 180, y: 470 },
    box5: { x: 180, y: 640 },

    // Side boxes

    dupl1:    { x: 560, y: 10  },
    biblio:   { x: -200,  y: 640 },
   

    // Exclusion boxes (right of box2 and box4)
    excl1: { x: 560, y: 200 },
    excl2: { x: 560, y: 470 },

    // Footnote (below box5)
  };
};

// --- Initial Nodes Generator ---
const createInitialNodes = () => {
  const positions = getNodePositions();

  return [
    // --- Stage Labels (left side, vertical text) ---
    {
      id: 'label1',
      data: { label: <div style={stageLabelTextStyle}>Identification</div> },
      position: positions.label1,
      style: { ...stageLabelStyle, minHeight: 120 },
      ...nodeDefaults,
    },
    {
      id: 'label2',
      data: { label: <div style={stageLabelTextStyle}>Screening</div> },
      position: positions.label2,
      style: { ...stageLabelStyle, minHeight: 270 },
      ...nodeDefaults,
    },
    {
      id: 'label3',
      data: { label: <div style={stageLabelTextStyle}>Eligibility</div> },
      position: positions.label3,
      style: { ...stageLabelStyle, minHeight: 150 },
      ...nodeDefaults,
    },
    {
      id: 'label4',
      data: { label: <div style={stageLabelTextStyle}>Included</div> },
      position: positions.label4,
      style: { ...stageLabelStyle, minHeight: 120 },
      ...nodeDefaults,
    },

    // --- Main Flow Boxes (vertical, top to bottom) ---
    {
      id: 'box1', type: 'editableNode',
      data: { label: '', count: 0, onEdit: null, handles: <><Handle type="source" position={Position.Bottom} id="bottom" style={{ bottom: 0, left: '50%', transform: 'translateX(-50%)', opacity: 0 }} /><Handle type="source" position={Position.Right} id="right" style={{ right: 0, top: '50%', transform: 'translateY(-50%)', opacity: 0 }} /></> },
      position: positions.box1, ...editableNodeDefaults,
    },
    {
      id: 'dupl1', type: 'editableNode',
      data: { label: '', count: 0, onEdit: null, handles: <Handle type="target" position={Position.Left} id="left" style={{ left: 0, top: '50%', transform: 'translateY(-50%)', opacity: 0 }} /> },
      position: positions.dupl1, ...editableNodeDefaults,
    },
    {
      id: 'box2', type: 'editableNode',
      data: { label: '', count: 0, onEdit: null, handles: <><Handle type="target" position={Position.Top} id="top" style={{ top: 0, left: '50%', transform: 'translateX(-50%)', opacity: 0 }} /><Handle type="source" position={Position.Bottom} id="bottom" style={{ bottom: 0, left: '50%', transform: 'translateX(-50%)', opacity: 0 }} /><Handle type="source" position={Position.Right} id="right" style={{ right: 0, top: '50%', transform: 'translateY(-50%)', opacity: 0 }} /></> },
      position: positions.box2, ...editableNodeDefaults,
    },
    {
      id: 'box4', type: 'editableNode',
      data: { label: '', count: 0, onEdit: null, handles: <><Handle type="target" position={Position.Top} id="top" style={{ top: 0, left: '50%', transform: 'translateX(-50%)', opacity: 0 }} /><Handle type="source" position={Position.Bottom} id="bottom" style={{ bottom: 0, left: '50%', transform: 'translateX(-50%)', opacity: 0 }} /><Handle type="source" position={Position.Right} id="right" style={{ right: 0, top: '50%', transform: 'translateY(-50%)', opacity: 0 }} /></> },
      position: positions.box4, ...editableNodeDefaults,
    },
    // Bibliographic and Conference Searching
    {
      id: 'biblio',
      type: 'biblioNode',
      data: { count: 0, onSave: null, onHover: null },
      position: positions.biblio,
      ...editableNodeDefaults,
    },
    {
      id: 'box5', type: 'editableNode',
      data: { label: '', count: 0, onEdit: null, handles: <><Handle type="target" position={Position.Top} id="top" style={{ top: 0, left: '50%', transform: 'translateX(-50%)', opacity: 0 }} /><Handle type="target" position={Position.Left} id="left" style={{ left: 0, top: '50%', transform: 'translateY(-50%)', opacity: 0 }} /></> },
      position: positions.box5, ...editableNodeDefaults,
    },

    // --- Exclusion Boxes (custom node type) ---
    {
      id: 'excl1',
      type: 'exclusionNode',
      data: { totalExcluded: 0, reasons: [] },
      position: positions.excl1,
      ...editableNodeDefaults,
    },
    {
      id: 'excl2',
      type: 'exclusionNode',
      data: { totalExcluded: 0, reasons: [] },
      position: positions.excl2,
      ...editableNodeDefaults,
    },

  ];
};

// --- Edges (vertical flow + horizontal branches to exclusion) ---
const initialEdges = [
  { id: 'e1',       source: 'box1',  target: 'box2',  sourceHandle: 'bottom', targetHandle: 'top',  type: 'straight', animated: true, markerEnd: edgeMarker, style: edgeStyle },
  { id: 'e_dupl',   source: 'box1',  target: 'dupl1', sourceHandle: 'right',  targetHandle: 'left', type: 'straight', animated: true, markerEnd: edgeMarker, style: edgeStyle },
  { id: 'e2',       source: 'box2',  target: 'excl1', sourceHandle: 'right',  targetHandle: 'left', type: 'straight', animated: true, markerEnd: edgeMarker, style: edgeStyle },
  { id: 'e3',       source: 'box2',  target: 'box4',  sourceHandle: 'bottom', targetHandle: 'top',  type: 'straight', animated: true, markerEnd: edgeMarker, style: edgeStyle },
  { id: 'e5',       source: 'box4',  target: 'excl2', sourceHandle: 'right',  targetHandle: 'left', type: 'straight', animated: true, markerEnd: edgeMarker, style: edgeStyle },
  { id: 'e6',       source: 'box4',  target: 'box5',  sourceHandle: 'bottom', targetHandle: 'top',  type: 'straight', animated: true, markerEnd: edgeMarker, style: edgeStyle },
  { id: 'e_biblio', source: 'biblio',target: 'box5',  sourceHandle: 'right',  targetHandle: 'left', type: 'straight', animated: true, markerEnd: edgeMarker, style: edgeStyle },
];

// --- FitView Options ---
const fitViewOptions = {
  padding: 0.12,
  maxZoom: 1.5,
  minZoom: 0.2,
};

const PrismaModal = ({ show, onClose, fabricId }) => {
  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [prismaData, setPrismaData] = useState(null);
  const [fabricName, setFabricName] = useState('');
  const [containerDimensions, setContainerDimensions] = useState({ width: 1600, height: 900 });
  const [nodes, setNodes] = useState(() => createInitialNodes());
  const [edges, setEdges] = useState(initialEdges);
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useRef(null);
  const [diagramReady, setDiagramReady] = useState(false);

  // Processing Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isMigrating, setIsMigrating] = useState(false);
  
  // Success Popup states
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

    // Inline editing states
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Export edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [exportFormat, setExportFormat] = useState('png');

  // Zoom control — disabled when hovering biblio box
  const [zoomEnabled, setZoomEnabled] = useState(true);

  // Biblio box state
  const [biblioEditing, setBiblioEditing] = useState(false);
  const [biblioHovered, setBiblioHovered] = useState(false);
  const [biblioCount, setBiblioCount] = useState(0);
  const [biblioSaved, setBiblioSaved] = useState(false);
  const biblioDebounceRef = useRef(null);
  const biblioLocalRef = useRef(0);

  // Custom node types (memoized to prevent re-registration)
  const nodeTypes = useMemo(() => ({ exclusionNode: ExclusionNode, biblioNode: BiblioNode, editableNode: EditableNode }), []);

  // API base URL
  const API_BASE_URL = '/pceapi/slr';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Measure container dimensions
  useEffect(() => {
    if (reactFlowWrapper.current && show) {
      const { width, height } = reactFlowWrapper.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }
  }, [show]);

  useEffect(() => {
    if (show && !prismaData && fabricId) {
      fetchPrismaData();
    }
    if (!show) {
      setPrismaData(null);
      setError(null);
      setFabricName('');
      setNodes(createInitialNodes());
      setEdges(initialEdges);
      setDiagramReady(false);
      setIsProcessingPopupVisible(false);
    }
  }, [show, fabricId]);

  useEffect(() => {
    if (prismaData) {
      let updatedNodes = createInitialNodes();
      const positions = getNodePositions();

      const updateNodeLabel = (id, newText, isBold = false) => {
        const node = updatedNodes.find(n => n.id === id);
        if (node) {
          const originalNodes = createInitialNodes();
          const originalNode = originalNodes.find(n => n.id === id);
          const handles = Array.isArray(originalNode.data.label.props?.children)
            ? originalNode.data.label.props.children.filter(child => child?.type?.$$typeof)
            : [];

          node.data.label = (
            <>
              {handles}
              <div
                className={isBold ? "node-label ev-bold-reg" : "node-label"}
                dangerouslySetInnerHTML={{ __html: newText }}
                onDoubleClick={() => setEditingField(id)}
                style={{ cursor: 'pointer' }}
                title="Double-click to edit"
              />
            </>
          );
        }
      };

      // Helper to get edited value or fallback to API value
      const val = (key, apiVal) => editValues[key] !== undefined ? editValues[key] : (apiVal ?? 0);

      // Footnote diff (computed early so box3 asterisk is conditional)
      const pdfDiff = val('box3_n', prismaData.p1_records_included) - val('box4_n', prismaData.p2_full_text_assessed);
      const asterisk = pdfDiff > 0 ? '*' : '';

      const pubmedLine = prismaData.p1_pubmed_count > 0 ? ` (n = ${prismaData.p1_pubmed_count})` : '';
      const manualLine = prismaData.p1_manual_count > 0 ? `Manual Upload (n = ${prismaData.p1_manual_count})` : '';
      const sourceLines = [pubmedLine, manualLine].filter(Boolean).join('<br />');

      const updateEditableNode = (id, label, count) => {
        const node = updatedNodes.find(n => n.id === id);
        if (node) {
          node.data = {
            ...node.data,
            label,
            count,
            isEditMode,
            onEdit: (newLabel, newCount) => {
              setEditValues(prev => ({ ...prev, [id]: newCount, [`${id}_text`]: newLabel }));
            }
          };
        }
      };

      const valText = (key, defaultText) => editValues[`${key}_text`] !== undefined ? editValues[`${key}_text`] : defaultText;

      updateEditableNode('box1', valText('box1', sourceLines ? `Records identified during database searching<br />${sourceLines}` : `Records identified during database searching`), val('box1', prismaData.p1_records_identified));
      updateEditableNode('dupl1', valText('dupl1', `Duplicates removed`), val('dupl1', prismaData.p1_duplicates_removed));
      updateEditableNode('box2', valText('box2', `Records screened based on title and abstract`), val('box2', prismaData.p1_records_screened));
      updateEditableNode('box4', valText('box4', `Full text publications assessed for eligibility`), val('box4', prismaData.p2_full_text_assessed));
      updateEditableNode('box5', valText('box5', `Included publications`), val('box5', prismaData.p2_studies_included));

      const excl1Node = updatedNodes.find(n => n.id === 'excl1');
      if (excl1Node) {
        excl1Node.data = { totalExcluded: val('excl1', prismaData.p1_records_excluded), reasons: editValues['excl1_reasons'] || prismaData.p1_exclusion_reasons || [], isEditMode, onEdit: (newCount) => setEditValues(prev => ({ ...prev, excl1: newCount })) };
      }
      const excl2Node = updatedNodes.find(n => n.id === 'excl2');
      if (excl2Node) {
        excl2Node.data = { totalExcluded: val('excl2', prismaData.p2_full_text_excluded), reasons: editValues['excl2_reasons'] || prismaData.p2_exclusion_reasons || [], isEditMode, onEdit: (newCount) => setEditValues(prev => ({ ...prev, excl2: newCount })) };
      }

      const biblioNodeRef = updatedNodes.find(n => n.id === 'biblio');
      if (biblioNodeRef) {
        const initCount = val('biblio', prismaData.p2_bibliographic_count);
        setBiblioCount(initCount);
        biblioNodeRef.data = {
          count: initCount,
          isEditing: biblioEditing,
          isEditMode,
          saved: biblioSaved,
          onChange: (newVal) => {
            biblioLocalRef.current = newVal;
            if (biblioDebounceRef.current) clearTimeout(biblioDebounceRef.current);
            biblioDebounceRef.current = setTimeout(() => {
            }, 500);
          },
          onBlur: () => {
            setEditValues(prev => ({ ...prev, biblio: biblioLocalRef.current }));
            setBiblioCount(biblioLocalRef.current);
            setBiblioEditing(false);
            setBiblioSaved(true);
            setTimeout(() => setBiblioSaved(false), 2000);
          },
        };
      }
      setNodes(updatedNodes);
      setEdges(initialEdges);
    }
  }, [prismaData, containerDimensions, editValues, biblioEditing, biblioHovered, biblioSaved, isEditMode]);

  // --- Fix overlap + align excl boxes so arrows are straight horizontal ---
  useEffect(() => {
    if (!prismaData || !diagramReady) return;

    const GAP = 30;

    const timer = setTimeout(() => {
      const zoom = reactFlowInstance.current ? reactFlowInstance.current.getZoom() : 1;

      const getInnerHeight = (dataId) => {
        const el = document.querySelector(`[data-id="${dataId}"] .nodrag.nopan`);
        const outer = document.querySelector(`[data-id="${dataId}"]`);
        const target = el || outer;
        return target ? target.getBoundingClientRect().height / zoom : 80;
      };

      const box2El = document.querySelector('[data-id="box2"]');
      const excl1El = document.querySelector('[data-id="excl1"]');

      if (!box2El || !excl1El) return;

      const box2Height = getInnerHeight('box2');
      const box4Height = getInnerHeight('box4');
      const box5Height = getInnerHeight('box5');
      const excl1Height = getInnerHeight('excl1');
      const excl2Height = getInnerHeight('excl2');
      const basePositions = getNodePositions();

      setNodes(prev => {
        const updated = prev.map(n => ({
          ...n,
          position: { ...n.position },
          style: n.style ? { ...n.style } : undefined,
        }));

        const find = id => updated.find(n => n.id === id);
        const box1Node = find('box1');
        const box2Node = find('box2');
        const box4Node = find('box4');
        const excl1Node = find('excl1');
        const excl2Node = find('excl2');
        const label2Node = find('label2');
        const dupl1Node = find('dupl1');

        if (!box2Node || !box4Node || !excl1Node || !excl2Node) return prev;

        // 0. Align dupl1 center with box1 center → straight horizontal line
        if (box1Node && dupl1Node) {
          const box1El = document.querySelector('[data-id="box1"]');
          const box1Height = box1El ? box1El.getBoundingClientRect().height / zoom : 60;
          const dupl1El = document.querySelector('[data-id="dupl1"]');
          const dupl1Height = dupl1El ? dupl1El.getBoundingClientRect().height / zoom : 60;
          const box1CenterY = box1Node.position.y + box1Height / 2;
          dupl1Node.position.y = box1CenterY - dupl1Height / 2;
        }

      // 0b. Force all main boxes to same X center
        const mainBoxX = 180;
        ['box1', 'box2', 'box4', 'box5'].forEach(id => {
          const node = find(id);
          if (node) node.position.x = mainBoxX;
        });

        

        // 1. Align excl1 center with box2 center → straight horizontal line
        const box2CenterY = box2Node.position.y + box2Height / 2;
        excl1Node.position.y = box2CenterY - excl1Height / 2;

        // 2. Check if excl1 overlaps where excl2 will be (centered on box4), shift if needed
        const excl1Bottom = excl1Node.position.y + excl1Height;
        // excl2 top when centered on box4 = box4.y + box4Height/2 - excl2Height/2
        const excl2TopIfUnshifted = basePositions.box4.y + box4Height / 2 - excl2Height / 2;
        const lowerBound = Math.min(excl2TopIfUnshifted, basePositions.box4.y);
        let offset = 0;

        if (excl1Bottom + GAP > lowerBound) {
          offset = (excl1Bottom + GAP) - lowerBound;

            ['box4', 'biblio', 'box5', 'label3', 'label4'].forEach(id => {
            const node = find(id);
            if (node && basePositions[id]) {
              node.position.y = basePositions[id].y + offset;
            }
          });

          if (label2Node && label2Node.style) {
            label2Node.style.minHeight = (parseInt(label2Node.style.minHeight) || 390) + offset;
          }
        }


        // 4. Align excl2 center with (possibly shifted) box4 center
        const box4CenterY = (find('box4')).position.y + box4Height / 2;
        excl2Node.position.y = box4CenterY - excl2Height / 2;

        // 5. Align biblio center with box5 center → straight horizontal line
        const biblioNode = find('biblio');
        const box5Node = find('box5');
        if (biblioNode && box5Node) {
          const box5El2 = document.querySelector('[data-id="box5"]');
          const box5HeightActual = box5El2 ? box5El2.getBoundingClientRect().height / zoom : 80;
          const biblioEl = document.querySelector('[data-id="biblio"]');
          const biblioHeight = biblioEl ? biblioEl.getBoundingClientRect().height / zoom : 60;
          const box5CenterY = box5Node.position.y + box5HeightActual / 2;
          biblioNode.position.y = box5CenterY - biblioHeight / 2;
        }

        return updated;
      });

      // Re-fit after repositioning
      if (reactFlowInstance.current) {
        reactFlowInstance.current.fitView({ duration: 300, ...fitViewOptions });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [prismaData, diagramReady]);

  useEffect(() => {
    if (show && diagramReady) {
      setTimeout(() => {
        const attribution = document.querySelector('.react-flow__attribution');
        if (attribution) attribution.style.display = 'none';
      }, 1000);
    }
  }, [show, diagramReady]);

  // Fetch PRISMA data
  const fetchPrismaData = async () => {
    setLoading(true);
    setError(null);
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading PRISMA Diagram');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/prisma_data`,
        { slr_fabric_id: fabricId },  
        { headers }
      );

      if (response.data.status === 'success') {
        const data = response.data.data;
        const prismaData = {
          // Phase 1
          p1_records_identified: data.phase1.records_identified || 0,
          p1_pubmed_count: data.phase1.pubmed_count || 0,
          p1_manual_count: data.phase1.manual_count || 0,
          p1_duplicates_removed: data.phase1.duplicates_removed || 0,
          p1_records_screened: data.phase1.records_screened || 0,
          p1_records_excluded: data.phase1.records_excluded || 0,
          p1_records_included: data.phase1.records_included || 0,
          p1_exclusion_reasons: data.phase1.exclusion_reasons || [],

          // Phase 2
          p2_full_text_assessed: data.phase2.full_text_assessed || 0,
          p2_full_text_excluded: data.phase2.full_text_excluded || 0,
          p2_studies_included: data.phase2.studies_included || 0,
          p2_studies_included_unique: data.phase2.studies_included_unique || 0,
          p2_exclusion_reasons: data.phase2.exclusion_reasons || [],
          p2_bibliographic_count: data.phase2.bibliographic_count || 0,
        };

        setPrismaData(prismaData);
        setFabricName(data.fabric_name);
      } else {
        setError(response.data.message || 'Failed to fetch PRISMA data.');
      }
    } catch (err) {
      console.error('Error fetching PRISMA data:', err);
      setError(err.response?.data?.message || err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
      setIsProcessingPopupVisible(false);
    }
  };

    // Save bibliographic count to DB
  const saveBiblioCount = useCallback(async (count) => {
    // API call removed for now — biblio count only saved in memory
  }, [fabricId]);

  // Download image helper
  const downloadImage = (dataUrl, format) => {
    const a = document.createElement('a');
    a.setAttribute('download', `prisma-diagram-${fabricName.replace(/\s+/g, '_') || fabricId}.${format}`);
    a.setAttribute('href', dataUrl);
    a.click();
  };

  // ============================================================
  // Smart crop: trims whitespace around actual diagram content
  // ============================================================
  const smartCrop = (dataUrl, padding = 50) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const { data, width, height } = imageData;

        // Get the background color from the top-left pixel
        const bgR = data[0], bgG = data[1], bgB = data[2];
        const tolerance = 5;

        const isBackground = (i) => {
          return Math.abs(data[i] - bgR) <= tolerance &&
                 Math.abs(data[i + 1] - bgG) <= tolerance &&
                 Math.abs(data[i + 2] - bgB) <= tolerance;
        };

        // Find bounding box of non-background content
        let minX = width, minY = height, maxX = 0, maxY = 0;

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            if (!isBackground(i)) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }

        // If no content found, return original
        if (minX >= maxX || minY >= maxY) {
          resolve(dataUrl);
          return;
        }

        // Apply padding, clamped to image bounds
        const cropX = Math.max(0, minX - padding);
        const cropY = Math.max(0, minY - padding);
        const cropW = Math.min(width - cropX, (maxX - minX) + padding * 2);
        const cropH = Math.min(height - cropY, (maxY - minY) + padding * 2);

        // Draw cropped region to new canvas
        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = cropW;
        croppedCanvas.height = cropH;
        const croppedCtx = croppedCanvas.getContext('2d');

        // Fill with background color first
        croppedCtx.fillStyle = `rgb(${bgR}, ${bgG}, ${bgB})`;
        croppedCtx.fillRect(0, 0, cropW, cropH);

        croppedCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

        resolve(croppedCanvas.toDataURL('image/png', 1.0));
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  };

  // ============================================================
  // SHARED EXPORT CAPTURE LOGIC
  // Expand excl boxes → measure → reposition if overlap → capture → restore
  // ============================================================
  const captureDiagram = useCallback(async () => {
    // 1. Save original nodes for restore
    const savedNodes = nodes.map(n => ({ ...n, position: { ...n.position }, data: { ...n.data } }));

    // 2. Hide controls, stop animations
    const attribution = document.querySelector('.react-flow__attribution');
    const controls = document.querySelector('.react-flow__controls');
    if (attribution) attribution.style.display = 'none';
    if (controls) controls.style.display = 'none';

    const edgeElements = document.querySelectorAll('.react-flow__edge-path');
    edgeElements.forEach(edge => edge.style.animation = 'none');

    const mainBoxes = document.querySelectorAll('.prisma-main-box');
    mainBoxes.forEach(el => el.classList.add('prisma-export-main-box'));

    await new Promise(resolve => setTimeout(resolve, 300));

    // 3. Measure excl node heights and check overlap
    const zoom = reactFlowInstance.current ? reactFlowInstance.current.getZoom() : 1;
    const excl1El = document.querySelector('[data-id="excl1"]');
    const excl2El = document.querySelector('[data-id="excl2"]');

    if (excl1El && excl2El) {
      const excl1Height = excl1El.getBoundingClientRect().height / zoom;
      const excl1Node = nodes.find(n => n.id === 'excl1');
      const excl2Node = nodes.find(n => n.id === 'excl2');

      if (excl1Node && excl2Node) {
        const excl1Bottom = excl1Node.position.y + excl1Height;
        const gap = 30;

        if (excl1Bottom + gap > excl2Node.position.y) {
          const offset = (excl1Bottom + gap) - excl2Node.position.y;
          const shifted = nodes.map(n => {
            if (n.id === 'excl2') {
              return { ...n, position: { ...n.position, y: n.position.y + offset } };
            }
            return n;
          });
          setNodes(shifted);
          await new Promise(resolve => setTimeout(resolve, 400));
        }
      }
    }

    // 4. Temporarily expand wrapper to large size for high-res capture
    const wrapper = reactFlowWrapper.current;
    if (!wrapper) throw new Error('Could not locate diagram wrapper.');

    const origStyle = wrapper.style.cssText;
    wrapper.style.width = '2100px';
    wrapper.style.height = '1400px';
    wrapper.style.position = 'absolute';
    wrapper.style.left = '0';
    wrapper.style.top = '0';

    await new Promise(resolve => setTimeout(resolve, 200));

    // 5. FitView into the large canvas
    if (reactFlowInstance.current) {
      reactFlowInstance.current.fitView({ padding: 0.08, includeHiddenNodes: false, duration: 0 });
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // 6. Find the react-flow element inside wrapper for capture
    const rfElement = wrapper.querySelector('.react-flow');
    if (!rfElement || rfElement.offsetWidth === 0 || rfElement.offsetHeight === 0) {
      wrapper.style.cssText = origStyle;
      throw new Error('Could not locate diagram. Please refresh and try again.');
    }

    const modalBody = document.querySelector('.prisma-diagram-background');
    const computedBgColor = modalBody
      ? window.getComputedStyle(modalBody).backgroundColor
      : '#ffffff';

    const rawDataUrl = await domtoimage.toPng(rfElement, {
      quality: 1.0,
      cacheBust: true,
      bgcolor: computedBgColor,
      filter: (node) => !node.classList?.contains('react-flow__controls') && !node.classList?.contains('react-flow__attribution'),
    });

    // 7. Smart crop to remove excess whitespace
    const dataUrl = await smartCrop(rawDataUrl, 50);

    // 8. Restore everything
    wrapper.style.cssText = origStyle;
    mainBoxes.forEach(el => el.classList.remove('prisma-export-main-box'));
    if (attribution) attribution.style.display = '';
    if (controls) controls.style.display = '';
    edgeElements.forEach(edge => edge.style.animation = '');
    setNodes(savedNodes);

    await new Promise(resolve => setTimeout(resolve, 300));
    if (reactFlowInstance.current) {
      reactFlowInstance.current.fitView({ duration: 300, ...fitViewOptions });
    }

    return dataUrl;
  }, [nodes]);

  // Restore helper for error paths
  const restoreAfterError = useCallback(() => {
    // Restore wrapper size if it was expanded
    if (reactFlowWrapper.current) {
      reactFlowWrapper.current.style.width = '100%';
      reactFlowWrapper.current.style.height = '100%';
      reactFlowWrapper.current.style.position = '';
      reactFlowWrapper.current.style.left = '';
      reactFlowWrapper.current.style.top = '';
    }

    const mainBoxes = document.querySelectorAll('.prisma-main-box');
    mainBoxes.forEach(el => el.classList.remove('prisma-export-main-box'));

    const attribution = document.querySelector('.react-flow__attribution');
    const controls = document.querySelector('.react-flow__controls');
    if (attribution) attribution.style.display = '';
    if (controls) controls.style.display = '';

    const edgeElements = document.querySelectorAll('.react-flow__edge-path');
    edgeElements.forEach(edge => edge.style.animation = '');

    if (reactFlowInstance.current) {
      reactFlowInstance.current.fitView({ duration: 0, ...fitViewOptions });
    }
  }, []);

  // Handle download
  const onDownload = async (format) => {
    setLoading(true);
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Downloading Image...');
    setError(null);

    try {
      setProcessingMessage('Preparing diagram...');
      await new Promise(resolve => setTimeout(resolve, 500));

      const pngDataUrl = await captureDiagram();

      if (pngDataUrl.length < 5000) {
        throw new Error('Generated image is too small. Export may have failed.');
      }

      // Convert to JPEG if needed
      let finalDataUrl = pngDataUrl;
      if (format === 'jpg') {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = pngDataUrl;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        finalDataUrl = canvas.toDataURL('image/jpeg', 1.0);
      }

      downloadImage(finalDataUrl, format);
      setProcessingMessage('Downloading Image...');
      await new Promise(resolve => setTimeout(resolve, 500));

    } catch (error) {
      console.error('Export error:', error);
      setError(`Export failed: ${error.message}`);
      restoreAfterError();
    } finally {
      setLoading(false);
      setIsProcessingPopupVisible(false);
    }
  };

  // Handle migrate to thread
  const handleMigrateToThread = async () => {
    try {
      setIsMigrating(true);
      setIsProcessingPopupVisible(true);
      setProcessingMessage('Capturing PRISMA diagram...');
      setError(null);

      const dataUrl = await captureDiagram();

      setProcessingMessage('Migrating to Data Collection...');

      // API call
      const response = await axios.post(
        '/pceapi/slr/migrate_prisma',
        {
          slr_fabric_id: fabricId,
          image_data: dataUrl
        },
        { headers }
      );

      if (response.data.status === 'success') {
        setIsProcessingPopupVisible(false);
        setSuccessMessage('PRISMA diagram migrated to Data Collection successfully!');
        setIsSuccessPopupVisible(true);
      } else {
        throw new Error(response.data.message || 'Migration failed');
      }

    } catch (error) {
      console.error('Migration error:', error);
      setError(`Migration failed: ${error.response?.data?.message || error.message}`);
      restoreAfterError();
    } finally {
      setIsMigrating(false);
      setIsProcessingPopupVisible(false);
    }
  };

  if (!show) return null;

  return createPortal(
    <>
      <div className="modal fade show d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000)" }}>
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content ev-message-card2" style={{ padding: '0', borderRadius: 'var(--px-15)' }}>
            <div className="modal-header" style={{ borderBottom: '0.5px solid var(--color9)', background: 'var(--background1)', borderRadius: 'var(--px-15) var(--px-15) 0 0' }}>
              <div className="d-flex align-items-center gap-3">
                <p className="mb-0 ev-fs-16 ev-bold-med ev-reg-p-dark">PRISMA Diagram</p>
                <span className="badge ev-fs-12" style={{ background: 'var(--color10)', color: 'var(--color2)', border: '1px solid var(--color2)' }}>
                  {fabricName || `Fabric ${fabricId}`}
                </span>
              </div>
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="btn ev-btn-blue-fill-round ev-btn-reg ev-bold-med"
                  onClick={handleMigrateToThread}
                  disabled={loading || error || !prismaData || !diagramReady || isMigrating}
                >
                  Migrate to Data Collection
                </button>

                                <div className="btn-group custom-btn-group-outer" role="group" aria-label="Export Options">
                  <span className="ev-fs-11 ev-bold-reg" style={{ padding: '0 10px 0 5px', color: 'var(--color4)', alignSelf: 'center' }}>
                    {isEditMode ? 'Editing' : 'Export'}
                  </span>
                  {!isEditMode ? (
                    <>
                      <button
                        type="button"
                        className="btn custom-btn-group-inner ev-btn-small ev-btn-white-fill-round"
                        onClick={() => { setExportFormat('jpg'); setIsEditMode(true); }}
                        disabled={loading || error || !prismaData || !diagramReady}
                        onMouseEnter={(e) => { if (!loading && !error && prismaData && diagramReady) { e.currentTarget.classList.remove('ev-btn-white-fill-round'); e.currentTarget.classList.add('ev-btn-blue-fill-round'); } }}
                        onMouseLeave={(e) => { e.currentTarget.classList.remove('ev-btn-blue-fill-round'); e.currentTarget.classList.add('ev-btn-white-fill-round'); }}
                      >JPG</button>
                      <button
                        type="button"
                        className="btn custom-btn-group-inner ev-btn-small ev-btn-white-fill-round"
                        onClick={() => { setExportFormat('png'); setIsEditMode(true); }}
                        disabled={loading || error || !prismaData || !diagramReady}
                        onMouseEnter={(e) => { if (!loading && !error && prismaData && diagramReady) { e.currentTarget.classList.remove('ev-btn-white-fill-round'); e.currentTarget.classList.add('ev-btn-blue-fill-round'); } }}
                        onMouseLeave={(e) => { e.currentTarget.classList.remove('ev-btn-blue-fill-round'); e.currentTarget.classList.add('ev-btn-white-fill-round'); }}
                      >PNG</button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn custom-btn-group-inner ev-btn-small ev-btn-blue-fill-round"
                                                onClick={async () => {
                          // Collect values directly from DOM inputs
                          const newEditValues = {};
                          document.querySelectorAll('.nodrag.nopan input[data-field]').forEach(input => {
                            const nodeEl = input.closest('[data-id]');
                            if (!nodeEl) return;
                            const nodeId = nodeEl.dataset.id;
                            const field = input.dataset.field;
                            if (field === 'label') {
                              newEditValues[`${nodeId}_text`] = input.value;
                            } else if (field === 'count') {
                              newEditValues[nodeId] = Number(input.value);
                            } else if (field.startsWith('reason_text_')) {
                              const idx = parseInt(field.split('_').pop());
                              if (!newEditValues[`${nodeId}_reasons`]) newEditValues[`${nodeId}_reasons`] = [];
                              if (!newEditValues[`${nodeId}_reasons`][idx]) newEditValues[`${nodeId}_reasons`][idx] = {};
                              newEditValues[`${nodeId}_reasons`][idx].reason = input.value;
                            } else if (field.startsWith('reason_count_')) {
                              const idx = parseInt(field.split('_').pop());
                              if (!newEditValues[`${nodeId}_reasons`]) newEditValues[`${nodeId}_reasons`] = [];
                              if (!newEditValues[`${nodeId}_reasons`][idx]) newEditValues[`${nodeId}_reasons`][idx] = {};
                              newEditValues[`${nodeId}_reasons`][idx].count = Number(input.value);
                            }
                          });
                          setEditValues(newEditValues);
                          setIsEditMode(false);
                          setTimeout(async () => {
                            await onDownload(exportFormat);
                            setEditValues({});
                            setPrismaData(null);
                            fetchPrismaData();
                          }, 300);
                        }}
                        disabled={loading}
                      >Export</button>
                      <button
                        type="button"
                        className="btn custom-btn-group-inner ev-btn-small ev-btn-white-fill-round"
                        onClick={() => {
                          setIsEditMode(false);
                          setEditValues({});
                          setPrismaData(null);
                          fetchPrismaData();
                        }}
                        disabled={loading}
                      >Cancel</button>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={onClose}
                  aria-label="Close"
                  style={{ filter: 'var(--btn-close-filter)' }}
                ></button>
              </div>
            </div>

            <div className="modal-body prisma-diagram-background ev-fs-10" style={{ height: '75vh', padding: '1rem', position: 'relative', background: 'var(--background2)', borderRadius: '0 0 var(--px-15) var(--px-15)' }}>
              {error && <div className="alert alert-danger" role="alert">Error: {error}</div>}

              {prismaData && (
                <ReactFlowProvider>
                  <div
                    ref={reactFlowWrapper}
                    id="prisma-export-area"
                    style={{ width: '100%', height: '100%' }}
                  >
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      nodeTypes={nodeTypes}
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable={false}
                      panOnDrag={true}
                      zoomOnScroll={zoomEnabled}
                      zoomOnDoubleClick={false}
                      zoomOnPinch={true}
                      panOnScroll={false}
                      preventScrolling={true}
                      fitView
                      fitViewOptions={fitViewOptions}
                      style={{ width: '100%', height: '100%' }}
                      onNodeClick={(e, node) => {
                        if (node.id === 'biblio') {
                          setBiblioEditing(true);
                          setZoomEnabled(false);
                        }
                      }}
                      onNodeMouseEnter={(e, node) => {
                        if (node.id === 'biblio') setZoomEnabled(false);
                      }}
                      onNodeMouseLeave={(e, node) => {
                        if (node.id === 'biblio') setZoomEnabled(true);
                      }}
                      onInit={(instance) => {
                        reactFlowInstance.current = instance;
                        instance.fitView({ duration: 0, ...fitViewOptions });
                        setTimeout(() => {
                          instance.fitView({ duration: 0, ...fitViewOptions });
                          setDiagramReady(true);
                        }, 3000);
                      }}
                    >
                      <Controls showInteractive={false} />
                    </ReactFlow>
                  </div>
                </ReactFlowProvider>
              )}

              {!loading && !error && !prismaData && show && (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <p className="ev-reg-p-dark">No PRISMA data available for this fabric yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={() => setIsSuccessPopupVisible(false)} 
          message={successMessage} 
        />
      )}
    </>,
    document.body
  );
};

export default PrismaModal;